import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { Amcharts } from './Amcharts';

export class AmchartsBarchart extends Amcharts {
    constructor() {
        super();

        this.whenReady((_ev: any) => {
            this.chart.events.on("datavalidated", (_e: any) => {
                this.chart.xAxes.each((valueAxis: any) => {
                    valueAxis.zoomable = false;
                    valueAxis.toggleZoomOutButton = false;
                });
            });
        });
    }

    createByRef(ref: any, context: any): any {
        this.create(ref, am4charts.XYChart, context);
        this.applyTheme(context);
        this.createAxis(context);
        this.updateValueAxes(context);

        if (context.dimensions.length) {
            let dimension = context.dimensions[0];
            let dimensionResultName = dimension.resultName || "key0";
            let categoryAxis = this.createCategoryAxis(dimensionResultName, context);
            categoryAxis.renderer.inversed = true;
        }
        this.createSerieses(context);
        this.updateMeasures(context);

        if (this.shouldUseCursor(context)) {
            let cursor = new am4charts.XYCursor();
            cursor.behavior = "panXY";
            this.chart.cursor = cursor;
        }

        //this._applyScrollbar(context.settings?.labelMaxWidth);

        return this;
    }

    //recreate(ref: any, context: any) {
    //    this.dispose();
    //    this.createByRef(ref, context);
    //}

    createAxis(context: any) {
        let isGlobalAxisExist = false;
        let isGlobalDeltaAxisExist = false;
        let measures = this.context.isCluster ? context.basicMeasures : context.measures;

        measures?.forEach((measure: any, i: any) => {
            const measureResultName = measure.resultName || `value${i}`;
            const measureAppearance = context.settings.measures?.find((m: any) => m.name === measureResultName) || {};

            const measureContext = {
                measure,
                measureAppearance,
                settings: context.settings,
                culture: context.culture
            };

            let isDeltaPercent = measure?.valueKind == 2;

            if (isDeltaPercent) {
                if (measureAppearance?.hasOwnAxis || !isGlobalDeltaAxisExist) {
                    this.createDeltaValueAxis(measureContext);
                    isGlobalDeltaAxisExist = true;
                }
            }
            else if (measureAppearance.hasOwnAxis || !isGlobalAxisExist) {
                this.createValueAxis(measureContext);
                isGlobalAxisExist = true;
            }
        });
    }

    createDeltaValueAxis(context: any) {
        let valueAxis = this.createValueAxis(context);

        valueAxis.dataFields.deltaName = "_delta_" + context.measure.resultName;
        valueAxis.dataFields.hasDelta = true;

        if (valueAxis.numberFormatter.numberFormat.includes('a'))
            valueAxis.numberFormatter.numberFormat = "#,###.";

        this.showValueAxisTooltip(valueAxis, context.settings.showValueAxisTooltip);

        return valueAxis;
    }

    createValueAxis(context: any) {
        let measure = context?.measure;
        let measureAppearance = context?.measureAppearance;
        let settings = context?.settings;

        let valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());
        valueAxis.title.text = this.translateFromSettings(context, "valueAxisTitle");
        valueAxis.dataFields.name = measure?.name;
        valueAxis.dataFields.resultName = measure?.resultName;

        valueAxis.numberFormatter = new am4core.NumberFormatter();
        valueAxis.max = settings.axisMaxValue;
        valueAxis.min = settings.axisMinValue;
        if (context.settings.axisGridDistance != null)
            valueAxis.renderer.minGridDistance = context.settings.axisGridDistance;

        if (context.settings?.stacked && context.settings?.percentage)
            valueAxis = this.setPercentageAxis(valueAxis);
        else {
            valueAxis.numberFormatter.numberFormat = this.getMeasureNumberFormat(measureAppearance, settings);
            valueAxis.numberFormatter.bigNumberPrefixes = this.getBigNumberPrefixes(measureAppearance, settings);
        }

        valueAxis.showOnInit = true;
        valueAxis.keepSelection = true;

        this.showValueAxisTooltip(valueAxis, context.settings.showValueAxisTooltip);
        this.setFixedBigNumberValueAxis(valueAxis, measureAppearance, settings);
        this.setLabelVisibility(valueAxis, settings);
        this.setCustomLabelsIfNeeded(valueAxis, measureAppearance);

        return valueAxis;
    }

    updateValueAxes(context: any) {
        for (let i = 0; i < context.measures.length; i++) {
            let measure = context.measures[i];
            let measureResultName = measure.resultName || "value" + i;
            let measureAppearance = context.settings.measures?.length
                ? context.settings.measures?.find((m: any) => m.name === measure.resultName)
                : {};
            let isOpposite = measureAppearance?.hasOwnAxis ? measureAppearance.axisPosition === "opposite" : false;

            let xAxis = this.chart.xAxes?.values?.find((axis: any) => axis.dataFields.resultName === measureResultName);

            if (xAxis !== undefined && xAxis !== null)
                xAxis.renderer.opposite = isOpposite;

            this.updateValueAxis(context, xAxis, measure);
        }

        this.baseSyncAxesByZero(context, this.chart.xAxes);
    }

    createCategoryAxis(categoryFieldName: any, context: any) {
        let categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());
        categoryAxis.dataFields.category = categoryFieldName;
        categoryAxis.tooltip.label.maxWidth = 200;
        categoryAxis.tooltip.label.truncate = true;
        categoryAxis.cursorTooltipEnabled = false;
        categoryAxis.renderer.labels.template.adapter.add("text", (label: any, target: any) => {
            if (label)
                label = label?.replace('{', '')?.replace('}', '');

            return this.dateCategoryFormatter(label);
        });

        this.updateCategoryAxisSettings(categoryAxis, context);
        return categoryAxis;
    }

    removeCategoryAxis(index: any) {
        this.chart.yAxes.removeIndex(index);
    }

    createSerieses(context: any) {
        let measures = context.isReorderStacked ? context.reorderStackedMeasures : context.measures;
        let dimensions = context.dimensions;

        for (let i = 0; i < measures.length; i++) {
            let measure = measures[i];
            let valueFieldName = measure.resultName || "value" + i;
            let isDelta = measure?.valueKind != 0;

            for (let j = 0; j < dimensions.length; j++) {
                let dimension = dimensions[j];
                let dimensionResultName = dimension.resultName || "key" + j;
                this.createSeries(valueFieldName, dimensionResultName, context, isDelta, i);
            }
        }
    }

    createSeries(valueFieldName: any, categoryFieldName: any, context: any, canUseDelta: any, i: any, keyDimensionName: any = null, seriesTitle: any = null) {
        const definedFieldName = canUseDelta ? "_delta_" + valueFieldName : valueFieldName;
        const { settings, measures, dimensions, isCluster } = context;
        const data = this.chart.data;
        const seriesType = this.getSeriesType(valueFieldName, settings);
        const measure = measures.find((f: any) => f.resultName == valueFieldName);
        const measureAppearance = this.context.isCluster ? settings.measures[0] : settings.measures.find((f: any) => f.name == valueFieldName);
        const bigPrefixes = this.getBigNumberPrefixes(measureAppearance, settings);
        const basicMeasures = context.basicMeasures ?? measures;
        const dimensionAppearance = settings?.dimensions?.find((d: any) => categoryFieldName.includes(d.name));
        const additional = { dimensionAppearance, valueFieldName, definedFieldName, categoryFieldName, keyDimensionName, seriesTitle }

        const blendSetting = context.blendSettings?.find((b: any) => {
            return b.measures.find((m: any) => {
                return m.name === valueFieldName;
            });
        });

        let numberFormat = this.getMeasureNumberFormat(measureAppearance, blendSetting ?? settings);
        numberFormat += this.percentSignIfNeeded(numberFormat, measure, canUseDelta, basicMeasures, isCluster, i);

        let series = this.createSeriesByType(seriesType, measure, numberFormat, bigPrefixes, blendSetting ?? settings, valueFieldName);

        //const dimension = dimensions.find((d: any) => d.resultName == categoryFieldName);
        //const dimensionIsDateTime = dimension?.dataType == 3;
        const category = /*dimensionIsDateTime ? "dateY" :*/ "categoryY";
        const valueFormat = settings.stacked && settings.percentage ? "{valueX.totalPercent.formatNumber(#'" + (numberFormat.match(/\.0+/)?.[0] ?? ".") + "')}%" : "{valueX.formatNumber('" + numberFormat + "')}";

        if (series instanceof am4charts.ColumnSeries)
            this.setSeriesColumnProperties(series, blendSetting ?? settings, category, valueFormat, measureAppearance, context, numberFormat, data, additional);
        else
            this.setSeriesLineProperties(series, blendSetting ?? settings, category, valueFormat, measureAppearance, context, data, additional);

        if (measureAppearance?.bulletsType === "last")
            this.setBulletsVisibility(data, measureAppearance, additional);

        this.setFixedBigNumberTooltip(series, measureAppearance, blendSetting ?? settings);
        //if (dimensionIsDateTime)
        //    series.dataFields.dateY = categoryFieldName;
        //else
        series.dataFields.categoryY = categoryFieldName;

        series.dataFields.valueX = definedFieldName;

        let hasOwnAxis = measureAppearance?.hasOwnAxis;

        series.xAxis = this.getAppropriateXAxis(definedFieldName, hasOwnAxis, canUseDelta);

        this.appendThreshold(context, series, valueFieldName, categoryFieldName, series.xAxis);

        // Value labels and circle bullets live in `setBullets`, which nothing but `updateSeries`
        // reaches. The call was dropped from here while both charts were restructured (CSH-197),
        // Column kept its own, and a bar has drawn no labels since. Same position as Column.
        this.updateSeries(context, valueFieldName, definedFieldName);

        series.xAxis = this.getAppropriateXAxis(definedFieldName, hasOwnAxis, canUseDelta);

        this.setSeriesSettings(series, blendSetting ?? settings, valueFormat, context, measureAppearance, i);
        this.applySeriesTheme(series, context, measureAppearance, i);

        return series;
    }

    createSeriesByType(seriesType: any, measure: any, numberFormat: any, bigPrefixes: any, settings: any, valueFieldName: any) {
        let series = !seriesType || seriesType == 'bar' ? this.chart.series.push(new am4charts.ColumnSeries()) : this.chart.series.push(new am4charts.LineSeries());
        series.name = this.getSeriesName(measure);
        series.numberFormatter = new am4core.NumberFormatter();
        series.numberFormatter.numberFormat = numberFormat;
        series.numberFormatter.bigNumberPrefixes = bigPrefixes;

        return series;
    }

    private setSeriesColumnProperties(series: any, settings: any, category: any, valueFormat: any, measureAppearance: any, context: any, numberFormat: any, data: any, additional: any) {
        let prefix = this.getPrefix(context, measureAppearance, settings);
        let suffix = this.getSuffix(context, measureAppearance, settings);
        let template = series.columns.template;
        const isStacked = settings?.stacked ?? false;
        const useCornerRadius = !isStacked;

        this.setCornerRadius(useCornerRadius, template.column);

        template.fillOpacity = .8;
        template.strokeWidth = 1;
        template.strokeOpacity = 1;
        template.percentHeight = settings.spanPercent == null ? 80 : settings.spanPercent;
        template.maxHeight = settings.maxSpanPixels == null ? undefined : settings.maxSpanPixels;
        const showLegendName = this.shouldPrefixLegendNameInTooltip(context);
        let tooltipText = showLegendName
            ? `{name} - ${this.dateCategoryFormatter(category)}: [bold]${prefix}${valueFormat}${suffix}[/]`
            : settings.isCluster
                ? `${this.dateCategoryFormatter(category)} - {name}: [bold]${prefix}${valueFormat}${suffix}[/]`
                : `${this.dateCategoryFormatter(category)}: [bold]${prefix}${valueFormat}${suffix}[/]`;

        if (settings.stacked && settings.percentage && settings.showMultiTooltips)
            series.tooltipText = tooltipText;
        else
            template.tooltipText = tooltipText;

        if (!context.settings.showMultiTooltips)
            series.cursorTooltipEnabled = false;

        series.stacked = isStacked;

        this.setBulletsVisibility(data, measureAppearance, additional);
        this.appendColumnActiveState(series);
        this.onClickColumnHandler(this.chart, series, context);
    }

    setSeriesLineProperties(series: any, settings: any, category: any, valueFormat: any, measureAppearance: any, context: any, data: any, additional: any) {
        let prefix = this.getPrefix(context, measureAppearance, settings);
        let suffix = this.getSuffix(context, measureAppearance, settings);
        let template = series;
        template.strokeWidth = 2;
        template.minBulletDistance = 0;

        if (measureAppearance)
            series.fillOpacity = measureAppearance.fillArea ? 0.3 : undefined;

        const namePrefix = this.shouldPrefixLegendNameInTooltip(context) ? "{name} - " : "";
        let tooltipText = `${namePrefix}${this.dateCategoryFormatter(category)}: [bold]${prefix}${valueFormat}${suffix}[/]`;

        //let bullet = series.bullets.push(new am4charts.CircleBullet());
        //bullet.circle.strokeWidth = 2;
        //bullet.circle.radius = 4;
        //bullet.circle.fill = am4core.color("#fff");
        //bullet.tooltipText = tooltipText;
        //let bullethover = bullet.states.create("hover");
        //bullethover.properties.scale = 1.3;

        if (settings.stacked && settings.percentage && settings.showMultiTooltips)
            series.tooltipText = tooltipText;
        else
            template.tooltipText = tooltipText;

        template.tooltip.pointerOrientation = "vertical";
        template.tooltip.label.textAlign = "middle";
        template.tooltip.label.textValign = "middle";
        this.setBulletsVisibility(data, measureAppearance, additional);
    }

    setSeriesSettings(series: any, settings: any, valueFormat: any, context: any, measureAppearance: any = null, measureIndex: number | null = null) {
        if (settings.stacked && settings.percentage) {
            series.calculatePercent = true;
            series.legendSettings.itemValueText = settings.showLegendPercent ? valueFormat : "";
            series.dataFields.valueXShow = "totalPercent";
        }

        let firstDimension = context.settings?.dimensions == undefined ? null : settings?.dimensions[0];
        const useMeasureGradient = measureAppearance?.useGradient === true;
        const useDimensionGradient = !measureAppearance?.theme && measureIndex === 0 && firstDimension?.useGradient;

        if (useMeasureGradient || useDimensionGradient)
            series.columns.template.adapter.add("fill", (fill: any, target: any) => target.dataItem
                ? this.getThemeColor(context, measureAppearance, measureIndex, target.dataItem.index) ?? fill
                : fill);
    }

    updateSeries(context: any, valueFieldName?: any, definedFieldName?: any) {
        let settings = context.settings;
        // Two different keys: the series carries `definedFieldName`, which is prefixed with
        // `_delta_` for a delta-kind measure, while the appearance below is keyed by the plain
        // measure name. Searching by the plain name found nothing for a delta measure and threw on
        // `valueSeries.columns`. The null guard also covers `AmchartsSignedBarchart`, which calls
        // this with no field name at all.
        let seriesFieldName = definedFieldName ?? valueFieldName;
        let valueSeries = this.chart.series._values.find((s: any) => s?.dataFields?.valueX === seriesFieldName);

        if (settings.measures != null && valueSeries != null) {
            let measureAppearance = this.context.isCluster ? settings.measures[0] : settings.measures.find((f: any) => f.name === valueFieldName);
            if (measureAppearance != null && valueSeries.columns != null)
                valueSeries.columns.template.fill = measureAppearance.color;
            this.setBullets(valueSeries, settings, measureAppearance, valueFieldName);
        }

        Amcharts.prototype.updateSeries.call(this, context);
    }

    setBulletsVisibility(data: any, measureAppearance: any, additional: any = null) {
        const { definedFieldName, categoryFieldName, keyDimensionName, seriesTitle, dimensionAppearance } = additional;
        if (data) {
            let disablePropName = "_disabled_" + definedFieldName;
            let dateCompared = dimensionAppearance?.threshold?.selectedDate ? new Date(dimensionAppearance.threshold.selectedDate) : new Date();
            for (let key in data) {
                let value = data[key];
                if (!seriesTitle || value[keyDimensionName] === seriesTitle) {
                    let itemDate = new Date(value[categoryFieldName]);
                    const maxDate = new Date(Math.max(...data.map((item: any) => new Date(item[categoryFieldName]))));
                    if (measureAppearance?.bulletsType === "last")
                        value[disablePropName] = itemDate < maxDate;
                    else if (this.isDimensionalThreshold(dimensionAppearance))
                        value[disablePropName] = itemDate < dateCompared
                    else if (this.isMeasureCustomThreshold(measureAppearance))
                        value[disablePropName] = value[measureAppearance.name] > measureAppearance?.thresholdCustomValue
                    else if (this.isMeasureNegativeThreshold(measureAppearance))
                        value[disablePropName] = value[measureAppearance.name] > 0
                }
            }
        }
    }

    setBullets(series: any, settings: any, measureAppearance: any, valueFieldName: any) {
        let circleBullet = series.bullets.push(new am4charts.CircleBullet());
        circleBullet.circle.strokeWidth = 2;
        circleBullet.circle.radius = 3;
        circleBullet.circle.fill = am4core.color(this.getBulletColor(measureAppearance, series));

        switch (measureAppearance?.bulletSize) {
            case "small":
                circleBullet.circle.radius = 2;
                break;
            case "medium":
                circleBullet.circle.radius = 3;
                break;
            case "large":
                circleBullet.circle.radius = 6;
                break;
            case "none":
            default:
                circleBullet.circle.radius = 0;
                break;
        }

        let bullethover = circleBullet.states.create("hover");
        bullethover.properties.scale = 1.3;

        let prefix = this.getPrefix(this.context, measureAppearance, settings);
        let suffix = this.getSuffix(this.context, measureAppearance, settings);
        let labelBullet = series.bullets.push(new am4charts.LabelBullet());
        labelBullet.label.dx = circleBullet.circle.radius + 2;
        let numberFormat = this.getMeasureNumberFormat(measureAppearance, settings);

        if (this.isNeedToShowLabels(this.showLabelsFromApp, this.context.showLabels) || this.context.showLabels) {
            labelBullet.label.text = `${prefix}{valueX.formatNumber('${numberFormat}')}${suffix}`;
            labelBullet.label.adapter.add("text", (text: any, target: any) => {
                if (!(settings.stacked && settings.percentage)) return text;

                const dataItem = target.dataItem as any;
                const percent = dataItem?.values?.valueX?.totalPercent;

                const rounded = Math.max(1, Math.round(percent));
                return percent == null ? "" : `${this.chart.numberFormatter.format(rounded, "#")}%`;
            });

            switch (measureAppearance?.bulletsType) {
                case "all":
                    labelBullet.disabled = false;
                    break;
                case "threshold":
                case "last":
                    labelBullet.disabled = true;
                    labelBullet.propertyFields.disabled = `_disabled_${valueFieldName}`;
                    break;
                default:
                    labelBullet.disabled = true;
                    delete labelBullet.propertyFields.disabled;
                    break;
            }

            //bulletsSize was previous name for labelSize.
            switch (measureAppearance?.bulletsSize) {
                case "small":
                    labelBullet.fontSize = 10;
                    break;
                case "medium":
                    labelBullet.fontSize = 14;
                    break;
                case "large":
                    labelBullet.fontSize = 16;
                    break;
            }

            labelBullet.label.fontWeight = 'bold';
            labelBullet.label.hideOversized = false;
            labelBullet.label.truncate = false;
            labelBullet.label.fill = am4core.color(measureAppearance?.labelColor ?? this.getDefaultLabelColor());

            this.addBulletAdapters(labelBullet, settings, measureAppearance, circleBullet.circle.radius);
        }
    }

    addBulletAdapters(labelBullet: any, settings: any, measureAppearance: any, bulletRadius: number = 3) {
        const position = measureAppearance?.labelBulletPosition ?? settings.labelBulletPosition;
        const distance = measureAppearance?.labelDistance ?? "medium";
        const showLines = measureAppearance?.labelShowLines ?? false;

        const isInside = position === 'inside';
        const offsetValue = this.getLabelDistanceOffset(distance);

        const lineLength = showLines && !isInside ? offsetValue : 0; //
        const totalOffset = isInside ? offsetValue : offsetValue + lineLength + 5; //
        const isHorizontal = true;

        this.createLabelLine(labelBullet, measureAppearance, showLines, isInside);
        this.addLabelPositionAdapter(labelBullet.label, isInside, isHorizontal);
        this.addLabelDxAdapter(labelBullet.label, isInside, offsetValue, totalOffset, 'valueX');

        if (showLines && !isInside) {
            this.addLineX1Adapter(labelBullet.line, bulletRadius, isHorizontal);
            this.addLineX2Adapter(labelBullet.line, bulletRadius, totalOffset, isHorizontal);
            this.addLineY1Adapter(labelBullet.line, bulletRadius, isHorizontal);
            this.addLineY2Adapter(labelBullet.line, bulletRadius, offsetValue, isHorizontal);
        }

        this.setFixedBigNumberLabel(labelBullet.label, measureAppearance, settings);
    }

    getSeriesType(measureName: any, settings: any) {
        if (settings && settings.measures) {
            let measureAppearance = this.context.isCluster ? settings.measures.find((m: any) => this.context.basicMeasures.find((x: any) => x.resultName == m.name)) : settings.measures.find((m: any) => measureName.includes(m.name));
            if (measureAppearance) {
                return measureAppearance.seriesType;
            }
        }
    }

    updateSettings(context: any) {
        super.updateSettings(context);

        let settings = context.settings;

        if (settings) {
            this.updateCategoryAxis(context);
            this.updateValueAxes(context);
            this.removeSerieses();
            this.createSerieses(context);
            this.updateMeasures(context);
            this.updateDimensions(settings);
            this.setLegend(context);

            //this._applyScrollbar(settings.labelMaxWidth);
        }
    }

    updateDimensions(settings: any) {
        if (settings == null || settings.dimensions == null) return;

        let dimensions = settings.dimensions;
        if (settings.dimensions.length) {
            for (let dimension of dimensions) {
                for (let j = 0; j < this.chart.yAxes.length; j++) {
                    let yAxis = this.chart.yAxes.getIndex(j);
                    if (yAxis.dataFields.category == dimension.name) {
                        yAxis.title.text = dimension.title;
                    }
                }
            }
        }
    }

    updateMeasures(context: any) {
        let settings = context.settings || {};
        if (!settings?.measures) return;

        const measureAppearances = settings.measures;
        if (measureAppearances.length) {
            for (const measureAppearance of measureAppearances) {
                for (let j = 0; j < this.chart.series.length; j++) {
                    const serie = this.chart.series.getIndex(j);
                    if (serie.dataFields.valueX.includes(measureAppearance.name)) {
                        let appearanceTitle = this.translateFromMeasure(context, measureAppearance, "title");
                        let metaFieldTitle = context.measureMetaDataFields.find((f: any) => f.resultName == measureAppearance.name)?.title;
                        serie.name = !appearanceTitle ? metaFieldTitle : appearanceTitle;
                    }
                }
            }
        }
    }

    updateCategoryAxisSettings(categoryAxis: any, context: any) {
        let dimension = context.settings?.dimensions?.length ? context.settings.dimensions?.filter((d: any) => d.name != null && d.categoryLabelRotation)[0] : {};
        categoryAxis.renderer.minGridDistance = dimension?.categoryLabelDistance || 30;

        categoryAxis.renderer.grid.template.location = 0;
        categoryAxis.title.fontWeight = "bold";
        categoryAxis.renderer.cellStartLocation = 0.1;
        categoryAxis.renderer.cellEndLocation = 0.9;
        categoryAxis.renderer.grid.template.location = 0;

        let label = categoryAxis.renderer.labels.template;
        label.html = null;
        label.wrap = false;
        label.truncate = false;
        label.maxWidth = context.settings.labelMaxWidth;
        if (label.maxWidth) {
            label.truncate = context.settings.labelTruncateText;
            label.wrap = !label.truncate;

            if (label.truncate) {
                label.tooltipText = label.truncate ? `${this.dateCategoryFormatter(`category`)}` : null;
                // Force the tooltip bubble to always display on the right
                label.tooltip = new am4core.Tooltip();
                label.tooltip.pointerOrientation = "left";
                // Only show tooltip if it is oversized
                label.adapter.add("tooltipText", function (text: string, target: any) {
                    return target && !target.isOversized ? null : text;
                });
            }
        }
        label.horizontalCenter = "right";
        label.verticalCenter = "middle";
        label.textAlign = "end";
        label.minWidth = context.settings.yAxisLabelWidth;

        let isCluster = context.isCluster;
        categoryAxis.renderer.cellStartLocation = isCluster ? context.cellStartLocation : 0;
        categoryAxis.renderer.cellEndLocation = isCluster ? context.cellEndLocation : 1;
        categoryAxis.title.fontWeight = "bold";
        let firstDimension = context.settings?.dimensions?.length ? context.settings.dimensions[0] : undefined;
        categoryAxis.numberFormatter = new am4core.NumberFormatter();
        categoryAxis.numberFormatter.numberFormat = this.getDimensionNumberFormat(firstDimension);
        categoryAxis.cursorTooltipEnabled = false;
    }

    updateCategoryAxis(context: any) {
        if (context.settings == null)
            return;

        let categoryAxis = this.chart.yAxes.getIndex(0);
        if (categoryAxis)
            this.updateCategoryAxisSettings(categoryAxis, context);
    }

    updateValueAxis(context: any, valueAxis: any, measure: any = null) {
        if (context?.settings == null) return;

        let firstAxis = this.chart.xAxes?.values?.[0];
        let measureAppearance = context.settings.measures?.find((m: any) => m.name === measure.resultName);

        if (valueAxis) {
            valueAxis.title.fontWeight = "bold";
            valueAxis.title.text = this.translateFromMeasure(context, measureAppearance, "axisTitle");
            valueAxis.numberFormatter = new am4core.NumberFormatter();
            valueAxis.max = context.settings.axisMaxValue;
            valueAxis.min = context.settings.axisMinValue;
            if (context.settings.axisGridDistance != null)
                valueAxis.renderer.minGridDistance = context.settings.axisGridDistance;

            if (context.settings?.stacked && context.settings?.percentage)
                valueAxis = this.setPercentageAxis(valueAxis);
            else {
                valueAxis.numberFormatter.numberFormat = this.getMeasureNumberFormat(measureAppearance, context.settings);
                valueAxis.numberFormatter.bigNumberPrefixes = this.getBigNumberPrefixes(measureAppearance, context.settings);
            }
        }
        if (valueAxis && valueAxis === firstAxis)
            valueAxis.title.text = this.translateFromMeasure(context, measureAppearance, "axisTitle") || this.translateFromSettings(context, "valueAxisTitle");
    }

    setData(context: any) {
        if (context.data) {
            this.chart.colors.reset();
            this.chart.data = context.data;

            if (context.data.length) {
                this.setLegendAndReorderStacked(context);
                this.updateValueAxes(context);
                this.syncValueAxes(context);
            }
        }
        else
            this.clearData();
    }

    markActiveByValue(values: any[]) {
        setTimeout(() => {
            this.chart.series.each((s: any) => s.columns.each((c: any, _i: number) => {
                values.forEach((v: any) => {
                    if (v == c.dataItem.categoryY)
                        c.isActive = true;
                })
            }));
        }, 100);
    }

    getAppropriateXAxis(valueFieldName: any, hasOwnAxis: boolean, isDeltaAxis: boolean) {
        if (hasOwnAxis || isDeltaAxis)
            return this.getActiveAxis(this.chart.xAxes, valueFieldName);
        else {
            const measureAppearancesWithOwnAxes = this.context?.settings?.measures?.filter((m: any) => m.hasOwnAxis);
            const measureAppearancesWithDeltaAxes = this.context?.measures?.filter((m: any) => m.valueKind != 0);

            const xAxesWithMultipleSpecial = this.getMarkedMultipleAxes(this.chart.xAxes, measureAppearancesWithOwnAxes,
                { measuresSettingsWithDeltaAxes: measureAppearancesWithDeltaAxes, mode: "special" });

            const appropriateAxis = xAxesWithMultipleSpecial.find((axis: any) => (axis !== null && axis !== undefined));
            return appropriateAxis ?? this.chart.xAxes.values[0];
        }
    }

    syncValueAxes(context: any) {
        const values = this.chart.xAxes.values;
        if (values?.length > 0) {
            const mainAxis = this.getMainValueAxis(context);
            this.baseSyncValueAxes(this.chart.xAxes, mainAxis);
        }
    }

    getMainValueAxis(context: any): any {
        const measureAppearancesWithOwnAxes = context?.settings?.measures?.filter((m: any) => m.hasOwnAxis);
        const measureAppearancesWithDeltaAxes = context?.measures?.filter((m: any) => m.valueKind != 0);

        const xAxesWithMultipleGeneral = this.getMarkedMultipleAxes(this.chart.xAxes, measureAppearancesWithOwnAxes,
            { measuresSettingsWithDeltaAxes: measureAppearancesWithDeltaAxes, mode: "general" });

        const mainAxis = xAxesWithMultipleGeneral.find((axis: any) => (axis !== null && axis !== undefined));
        return mainAxis ?? null;
    }

    removeValueAxisByResultName(resultName: string) {
        let xAxis = this.chart.xAxes?.values?.find((axis: any) => axis.dataFields.resultName === resultName);
        if (xAxis !== undefined) {
            let index = this.chart.yAxes.indexOf(xAxis);
            this.chart.yAxes.removeIndex(index);
        }
    }

    sortLegend(series: any) {
        series.events.once("ready", (ev: any) => {
            if (!ev.target.legendDataItem)
                return;

            let names = ev.target.chart.series.values.map((x: any) => x.name);
            let sortedNames = names.sort((x: any, y: any) => (x > y ? 1 : -1))
            let name = ev.target.legendDataItem.itemContainer.dataItem.name;
            console.log('name: ' + name);
            let index = sortedNames.indexOf(name) * 10;
            console.log('index: ' + index);

            ev.target.legendDataItem.itemContainer.zIndex = index;
            ev.target.chart.legend.children.moveValue(ev.target.legendDataItem.itemContainer, index);
        });
    }

    private _applyScrollbar(labelMaxWidth: number | null | undefined): void {
        if (labelMaxWidth) {
            if (!this.chart.scrollbarX)
                this.chart.scrollbarX = new am4core.Scrollbar();
            this.chart.scrollbarX.parent = this.chart.topAxesContainer;
        } else {
            this.chart.scrollbarX = null as any;
        }
    }
}
